import { useState, useCallback } from 'react';
import axios from 'axios';
import { EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import {
  UpdateNote,
  UpdateFilters,
  UpdateFormData
} from '../types/EducationHub';

// Custom hook for update notes management
export const useUpdates = () => {
  const [updates, setUpdates] = useState<UpdateNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getAdminHeaders = () => {
    const token = sessionStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchUpdates = useCallback(async (filters: UpdateFilters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.UPDATES, {
        params: filters,
        headers: getAuthHeaders()
      });

      // Backend returns updateNotes, not updates
      const data = response.data;
      setUpdates(data.updateNotes || []);
      setTotalCount(data.total || 0);
      setHasMore(data.hasMore || false);
    } catch (err: any) {
      // Always ensure updates is an array, never undefined
      setUpdates([]);
      setTotalCount(0);
      setHasMore(false);
      
      // Only log errors that are not authentication related
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Failed to fetch updates');
        console.error('Error fetching updates:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getLatestUpdate = useCallback(async (): Promise<UpdateNote | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.UPDATES_LATEST, {
        headers: getAuthHeaders()
      });

      return response.data;
    } catch (err: any) {
      // Only log errors that are not authentication related or 404 (no content)
      if (err.response?.status !== 401 && err.response?.status !== 403 && err.response?.status !== 404) {
        setError(err.response?.data?.error || 'Failed to fetch latest update');
        console.error('Error fetching latest update:', err);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Super-admin operations ──────────────────────────────────────────────────

  const fetchAdminUpdates = useCallback(async (): Promise<UpdateNote[]> => {
    const { data } = await axios.get(EDUCATION_ENDPOINTS.UPDATES_ADMIN, { headers: getAdminHeaders() });
    return data.updateNotes || [];
  }, []);

  const createUpdate = useCallback(async (updateData: UpdateFormData): Promise<UpdateNote | null> => {
    setError(null);
    try {
      const { data } = await axios.post(EDUCATION_ENDPOINTS.UPDATES, updateData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create update');
      throw err;
    }
  }, []);

  const updateUpdateNote = useCallback(async (id: string, updateData: Partial<UpdateFormData>): Promise<UpdateNote | null> => {
    setError(null);
    try {
      const { data } = await axios.put(EDUCATION_ENDPOINTS.UPDATES_ITEM(id), updateData, { headers: getAdminHeaders() });
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update update note');
      throw err;
    }
  }, []);

  const deleteUpdate = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await axios.delete(EDUCATION_ENDPOINTS.UPDATES_ITEM(id), { headers: getAdminHeaders() });
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete update note');
      throw err;
    }
  }, []);

  const getUpdateById = useCallback(async (id: string): Promise<UpdateNote | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${EDUCATION_ENDPOINTS.UPDATES}/${id}`, {
        headers: getAuthHeaders()
      });
      
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch update note');
      console.error('Error fetching update note:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUpdateByVersion = useCallback(async (version: string): Promise<UpdateNote | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${EDUCATION_ENDPOINTS.UPDATES}/version/${version}`, {
        headers: getAuthHeaders()
      });
      
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch update note');
      console.error('Error fetching update note:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updates,
    loading,
    error,
    totalCount,
    hasMore,
    fetchUpdates,
    getLatestUpdate,
    fetchAdminUpdates,
    createUpdate,
    updateUpdateNote,
    deleteUpdate,
    getUpdateById,
    getUpdateByVersion
  };
};

// Custom hook for update notes metadata
export const useUpdateMetadata = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.UPDATES_CATEGORIES, {
        headers: getAuthHeaders()
      });
      
      // Backend returns array directly, not wrapped in object
      setCategories(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch update categories');
      console.error('Error fetching update categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.UPDATES_TYPES, {
        headers: getAuthHeaders()
      });
      
      // Backend returns array directly, not wrapped in object
      setTypes(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch update types');
      console.error('Error fetching update types:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    categories,
    types,
    loading,
    error,
    fetchCategories,
    fetchTypes
  };
};
